import { useCallback, useEffect, useMemo, useState } from 'react';
import { EnabledStates, SelectedValues, useLazyGetBundleSectionsQuery } from '@/api';
import {
  selectBundleFilterOptions,
  selectEnabledStates,
  selectSelectedValues,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  setEnabledStates,
  setSelectedValues,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { getBundleSectionsBody, getSortedBundleFilterOptions } from '@/ui/pages/BundlePage/utils';
import {
  FILTER_BLANKS_KEY,
  FILTER_NON_BLANKS_KEY,
  FILTER_EXTRACTED_KEY,
  FILTER_DUPLICATES_KEY,
} from '@/constants/filters';

export const useBundleFilterOptions = () => {
  const [triggerBundleSections] = useLazyGetBundleSectionsQuery();

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundleFilterOptions = useAppSelector(selectBundleFilterOptions);
  const selectedValues = useAppSelector(selectSelectedValues);
  const enabledStates = useAppSelector(selectEnabledStates);

  const dispatch = useAppDispatch();

  /** local state to not change actual filters until the user presses the "Apply" button */
  const [localEnabledStates, setLocalEnabledStates] = useState<EnabledStates>(enabledStates);
  const [localSelectedValues, setLocalSelectedValues] = useState<SelectedValues>(selectedValues);

  useEffect(() => {
    setLocalEnabledStates(enabledStates);
  }, [enabledStates]);

  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  const sortedBundleFilterOptions = useMemo(
    () => getSortedBundleFilterOptions(bundleFilterOptions),
    [bundleFilterOptions],
  );

  const { selectedFilters, localSelectedFilters, totalFilters } = useMemo(() => {
    const counts = sortedBundleFilterOptions.reduce(
      (acc, option) => {
        if (option.displayType === 'dates') {
          return {
            selectedFilters: enabledStates[option.groupName] ? acc.selectedFilters + 1 : acc.selectedFilters,
            localSelectedFilters: localEnabledStates[option.groupName]
              ? acc.localSelectedFilters + 1
              : acc.localSelectedFilters,
            totalFilters: acc.totalFilters + 1,
          };
        }

        if (option.displayType === 'drop_down') {
          return {
            selectedFilters:
              enabledStates[option.groupName] && selectedValues[option.groupName]
                ? acc.selectedFilters + selectedValues[option.groupName].length
                : acc.selectedFilters,
            localSelectedFilters:
              localEnabledStates[option.groupName] && localSelectedValues[option.groupName]
                ? acc.localSelectedFilters + localSelectedValues[option.groupName].length
                : acc.localSelectedFilters,
            totalFilters: acc.totalFilters + option.options.length,
          };
        }

        if (option.displayType === 'checkbox') {
          return {
            selectedFilters: selectedValues[option.groupName]
              ? acc.selectedFilters + selectedValues[option.groupName].length
              : acc.selectedFilters,
            localSelectedFilters: localSelectedValues[option.groupName]
              ? acc.localSelectedFilters + localSelectedValues[option.groupName].length
              : acc.localSelectedFilters,
            totalFilters: acc.totalFilters + option.options.length,
          };
        }

        return acc;
      },
      { selectedFilters: 0, localSelectedFilters: 0, totalFilters: 0 },
    );

    // Count static boolean filters
    const staticKeys = [FILTER_BLANKS_KEY, FILTER_NON_BLANKS_KEY, FILTER_EXTRACTED_KEY, FILTER_DUPLICATES_KEY];
    const staticSelected = staticKeys.filter((k) => enabledStates[k]).length;
    const staticLocalSelected = staticKeys.filter((k) => localEnabledStates[k]).length;

    return {
      selectedFilters: counts.selectedFilters + staticSelected,
      localSelectedFilters: counts.localSelectedFilters + staticLocalSelected,
      totalFilters: counts.totalFilters,
    };
  }, [enabledStates, localEnabledStates, localSelectedValues, selectedValues, sortedBundleFilterOptions]);

  const handleCheckboxChange = useCallback(
    (groupName: string) => {
      const newEnabledState = !localEnabledStates[groupName];
      setLocalEnabledStates((prevState) => ({
        ...prevState,
        [groupName]: newEnabledState,
      }));

      if (!newEnabledState) {
        setLocalSelectedValues((prevState) => ({
          ...prevState,
          [groupName]: [],
        }));
      }
    },
    [localEnabledStates],
  );

  const handleValueChange = useCallback(
    (groupName: string, value: string | string[] | Date[]) => {
      setLocalSelectedValues({ ...localSelectedValues, [groupName]: value });
    },
    [localSelectedValues],
  );

  const handleDeselectAllFilters = useCallback(() => {
    const newEnabledStates = { ...localEnabledStates };
    Object.keys(newEnabledStates).forEach((key) => {
      newEnabledStates[key] = false;
    });
    setLocalEnabledStates(newEnabledStates);

    const newSelectedValues = { ...localSelectedValues };
    Object.keys(newSelectedValues).forEach((key) => {
      if (Array.isArray(newSelectedValues[key])) {
        newSelectedValues[key] = [];
      }
    });
    setLocalSelectedValues(newSelectedValues);
  }, [localEnabledStates, localSelectedValues]);

  const handleResetLocalState = useCallback(() => {
    setLocalEnabledStates(enabledStates);
    setLocalSelectedValues(selectedValues);
  }, [enabledStates, selectedValues]);

  const handleApply = useCallback(() => {
    if (!bundleId || !activeBundleType) {
      return;
    }

    const bundleSectionsBody = getBundleSectionsBody({
      bundleId,
      selectedValues: localSelectedValues,
      enabledStates: localEnabledStates,
      bundleFilterOptions: sortedBundleFilterOptions,
    });

    triggerBundleSections(bundleSectionsBody);

    dispatch(setEnabledStates({ key: activeBundleType, data: localEnabledStates }));
    dispatch(setSelectedValues({ key: activeBundleType, data: localSelectedValues }));
  }, [
    activeBundleType,
    bundleId,
    dispatch,
    localEnabledStates,
    localSelectedValues,
    sortedBundleFilterOptions,
    triggerBundleSections,
  ]);

  return {
    sortedBundleFilterOptions,
    selectedFilters,
    localSelectedFilters,
    totalFilters,
    localEnabledStates,
    localSelectedValues,
    handleCheckboxChange,
    handleValueChange,
    handleDeselectAllFilters,
    handleResetLocalState,
    handleApply,
  };
};
